import cookies from 'utils/cookies'

import TopLevelComponent from 'components/TopLevel'
import { canSeeDesignGuide, shouldSeeWelcomeSelector } from 'selectors/users'
import store from 'store'
import { Paths } from 'utils/paths'

const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === 'true'

export const
  redirectToSessionRoute = toProp => (nextState, replace) => {
    const
      { session } = store.getState(),
      toRoute = Paths[toProp]

    if(!toRoute) { throw new Error(`IOShortcut Error: No route generator named "${toProp}"`) }

    session?.user
      // if user, generate the requested route with their username
      ? replace(toRoute({ username: session.user.username, ...nextState.params }))
      // if no user, redirect to landing
      : replace(Paths.root())
  },

  forwardToHome = (nextState, replace) => {
    const user = store.getState().session?.user

    if (user?.username) {
      if (shouldSeeWelcomeSelector(store.getState())) {
        replace(Paths.welcome())
        return
      }
      replace(Paths.home({ username: user.username }))
    }
  },

  checkEveryPage = (nextState, replace) => {
    const nextPath = nextState.location.pathname

    // Check for maintenance mode
    if (MAINTENANCE_MODE && !/maintenance/.test(nextPath)) {
      replace('/maintenance')
      return
    }

    // Check for users without a username
    const user = store.getState().session?.user

    if (user && !user.username && (nextPath !== Paths.noUsername())) {
      // direct them immediately to noUsername
      replace(Paths.noUsername())
      return
    }

    // Check for redirect cookie
    const redir = cookies.get('redirect_to')

    if (redir) {
      cookies.remove('redirect_to')

      const pathname = decodeURIComponent(redir)
      if (Paths.isSafe(pathname)) {
        replace(pathname)
        return
      }
    }
  },

  // checks auth and redirects to root
  authOnly = (nextState, replace) => {
    const sessionUser = store.getState().session?.user

    if(!sessionUser) {
      replace(Paths.root())
      return
    }

    if (shouldSeeWelcomeSelector(store.getState()) && nextState.location.pathname !== Paths.welcome()) {
      replace(Paths.welcome())
      return
    }
  },

  // compare the username in the session to the username in the url
  // mismatches are redirected to the public page for the url username
  ownerOnly = (nextState, replace) => {
    const
      urlUsername = nextState.params.username,
      sessionUser = store.getState().session?.user,
      isAnon = !sessionUser,
      isNotUser = isAnon || (urlUsername !== sessionUser.username),
      hasAuthority = !isAnon && ['admin', 'developer'].includes(sessionUser.role)

    if (isNotUser && !hasAuthority) {
      replace(Paths.public_user({ username: urlUsername }))
      return
    }

    if (shouldSeeWelcomeSelector(store.getState()) && nextState.location.pathname !== Paths.welcome()) {
      replace(Paths.welcome())
    }
  },

  guideAccessOnly = (nextState, replace) => {
    canSeeDesignGuide(store.getState()) || replace(Paths.root())
  },

  wrapPage = page => async (_, callback) => {
    try {
      const view = await page()
      callback(null, { main: TopLevelComponent(view.default) })

    } catch(err) {
      if(err.name === "ChunkLoadError") {
        // TODO: handle this, gentle reload, tell sentry with extras, engage ErrorBoundary
      }

      // same behavior as now
      throw err
    }
  }
